import React, { useEffect, useMemo, useState } from 'react';
import BookingQuote from './BookingQuote';
import BookingConfirmation from './BookingConfirmation';
import BookingInvoice from './BookingInvoice';
import BookingReceipt from './BookingReceipt';
import { useAppContext } from '../../libs/contextLib';
import dayjs from '../../dayjs';
import { Card, Space, Tabs, Tag } from 'antd';
import DocumentActionsMenu from '../../components/DocumentActionsMenu';
import { QUOTE_STATUS } from '../../model/charter';
import { values } from 'lodash';
import fileDownload from 'js-file-download';
import AdmZip from 'adm-zip';

const BookingBilling = ({ mode, charter, setBookingWith, setBookingAndSave, mail, setPdfType, handlePrint, onDownloadClicked }) => {
    const { user, operator, messageApi } = useAppContext();
    const [preview, setPreview] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async ( files = [] ) => {
        setDownloading(true);
        try {
            if (onDownloadClicked) {
                files = await onDownloadClicked();
            }
            if(files.length === 0) {
                return
            }
            let file = files[0];
            if (files?.length > 1) {
                file = new AdmZip();
                files.forEach((file) => {
                    file.addFile(file.name, file);
                });
                fileDownload(file.toBuffer(), `Busable_${dayjs().format('YYYY-MM-DD_HH:mm')}.zip`, 'application/zip');
            }
            fileDownload(file, file.name, file.type);
            // send event back
            setDownloading(false);
        } catch (err) {
            console.log('Something went wrong: ', err);
            messageApi.error('Something went wrong while downloading files: ', err);
            setDownloading(false);
        }
    };

    const getHistoryLabel = (action, actionType) => {
        if (actionType === 'voidInvoice') {
            return `Voided invoice`;
        } else if (actionType === 'unmarkAsDeclined') {
            return `Unmarked quote as declined`;
        } else if (actionType === 'unmarkAsAccepted') {
            return `Unmarked quote as accepted`;
        } else if (actionType === 'markAsDeclined') {
            return `Marked quote as declined`;
        }
        switch (action) {
            case 'draft':
                return `Marked quote as draft`;
            case 'sent':
                return `Marked quote as sent`;
            case 'accepted':
                return `Marked quote as accepted`;
            case 'declined':
                return `Marked quote as declined`;
            case 'invoice-draft':
                return `Created invoice`;
            case 'invoice-approved':
                return `Approved invoice`;
            case 'awaiting-payment':
                return `Approved invoice`;
            case 'paid':
                return `Marked invoice as paid`;
            case 'voidedInvoice':
                return `Voided invoice`;

        }
    }

    const handleDocumentAction = (action) => {
        switch (action.type) {
            case 'setQuoteStatus':
                setBookingAndSave([
                    'quoteStatus',
                    'quoteHistory',
                    'status'
                ], [
                    action.payload.quoteStatus,
                    [...charter.quoteHistory, {
                        status: action.payload.quoteStatus,
                        date: dayjs().format('DD/MM/YYYY HH:mm'),
                        timestamp: dayjs().unix(),
                        label: getHistoryLabel(action.payload.quoteStatus, action.action),
                        user: `${user.attributes.given_name} ${user.attributes.family_name}`,
                        email: user.attributes.email
                    }],
                    action.payload.status
                ]);
                break;
            case 'acceptQuote':
                setBookingAndSave([
                    'quoteStatus',
                    'confirmationDetails',
                ], [
                    action.payload.status,
                    action.payload.confirmationDetails,
                ]);
                break;
            case 'declineQuote':
                setBookingAndSave([
                    'quoteStatus',
                    'confirmationDetails',
                ], [
                    action.payload.status,
                    action.payload.confirmationDetails,
                ]);
                break;
            case 'createInvoice':
                setBookingAndSave([
                    'quoteStatus',
                    'invoiceDetails',
                ], [
                    action.payload.status,
                    action.payload.invoiceDetails,
                ]);
                break;
            case 'approveInvoice':
                setBookingAndSave('quoteStatus', action.payload.status);
                break;
            case 'reminder':
                // Handle reminder action
                break;
            case 'viewQuote':
                setPreview('sent');
                break;
            case 'viewConfirmation':
                setPreview('accepted');
                break;
            case 'viewInvoice':
                setPreview('awaiting-payment');
                break;
            case 'cancelPreview':
                setPreview(null);
                break;
            case 'print':
                handlePrint();
                break;
            case 'download':
                handleDownload();
                break;
            case 'setReceiptDetails':
                setBookingAndSave('receiptDetails', action.payload);
                break;
            default:
                break;
        }
    };

    const activeTab = useMemo(() => {
        let key;
        switch (charter.quoteStatus) {
            case 'draft':
            case 'sent':
            case 'accepted':
            case 'declined':
                key = 'quote';
                break;
            case 'invoice-draft':
            case 'invoice-approved':
            case 'awaiting-payment':
                key = 'invoice';
                break;
            case 'paid':
                key = 'receipt';
                break;
            default:
                key = 'quote';
                break;
        }

        if(preview) {
            if(preview === key) {
                setPreview(null);
            } else {
                key = preview;
            }
        }

        setPdfType(key);
        return key;
    }, [preview, charter.quoteStatus]);

    const invoiceEnabled = useMemo(() => {
        return charter.quoteStatus === 'invoice-draft' || charter.quoteStatus === 'paid' || charter.quoteStatus === 'invoice-approved' || charter.quoteStatus === 'awaiting-payment';
    }, [charter.quoteStatus]);

    const receiptEnabled = useMemo(() => {
        return charter.quoteStatus === 'paid';
    }, [charter.quoteStatus]);

    const getQuoteStatus = (type) => {
        const statuses = values(QUOTE_STATUS);
      
        // Map of the highest quote status before invoice statuses
        const highestQuoteStatus = 'accepted';
      
        // All quote and invoice statuses
        const quoteStatuses = ['draft', 'sent', 'accepted', 'declined'];
        const invoiceStatuses = ['invoice-draft', 'awaiting-payment', 'paid'];
      
        let displayStatusValue = charter.quoteStatus;
      
        if (type === 'quote') {
          // If the current status is an invoice status, show the highest quote status instead
          if (invoiceStatuses.includes(charter.quoteStatus)) {
            displayStatusValue = highestQuoteStatus;
          }
          // If the current status is not a quote status, fallback to the first quote status
          if (!quoteStatuses.includes(displayStatusValue)) {
            displayStatusValue = quoteStatuses[0];
          }
        } else if (type === 'invoice' || type === 'receipt') {
          // If not an invoice status, fallback to the first invoice status
          if (!invoiceStatuses.includes(displayStatusValue)) {
            displayStatusValue = invoiceStatuses[0];
          }
        }
      
        const status = statuses.find(status => status.value === displayStatusValue) || statuses[0];
        return <Space className='d-flex align-items-center' style={{ gap: 10 }}>
        <span>
          {type === 'quote' ? 'Quote' :
              type === 'invoice' ? 'Invoice' : 'Receipt'}
        </span>
        {activeTab === type && (
            <div className='simple-tags'>
                <Tag color={status.color}>{status.label}</Tag>
            </div>
        )}
      </Space>
      };

    return (
        <Card
            bordered={false} 
            className="card-main"
        >
            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setPreview(key === activeTab ? null : key)
                }}
                type="card"
                className="card-tabs"
                tabBarExtraContent={
                    <DocumentActionsMenu
                        mode={mode}
                        charter={charter} 
                        onAction={handleDocumentAction}
                        currentView={activeTab}
                        preview={preview}
                        mail={mail}
                        type={activeTab === 'receipt'? 'invoice' : activeTab}
                        />
                }
            >
                <Tabs.TabPane tab={getQuoteStatus("quote")} key="quote">
                    <BookingQuote
                        mode={mode}
                        charter={charter}
                        setBookingWith={setBookingWith}
                        onAction={handleDocumentAction}
                        preview={preview}
                        mail={mail}
                    />
                </Tabs.TabPane>
                {invoiceEnabled && <Tabs.TabPane tab={getQuoteStatus("invoice")} key="invoice">
                    <BookingInvoice
                        mode={mode}
                        charter={charter}
                        setBookingWith={setBookingWith}
                        onAction={handleDocumentAction}
                        preview={preview}
                        mail={mail}
                    />
                </Tabs.TabPane>}
                {receiptEnabled && (
                    <Tabs.TabPane tab={getQuoteStatus("receipt")} key="receipt">
                        <BookingReceipt
                            mode={mode}
                            charter={charter}
                            setBookingWith={setBookingWith}
                            onAction={handleDocumentAction}
                            preview={preview}
                            mail={mail}
                        />
                    </Tabs.TabPane>
                )}
            </Tabs>
        </Card>
    )
};

export default BookingBilling;

